/**
 * Versatile SVG Shape generator (Rect, Circle, Ellipse, Polygon, Star, Grid).
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export type ShapeKind = 'rect' | 'circle' | 'ellipse' | 'polygon' | 'star' | 'grid' | 'roundedRect';

export interface ShapeProps {
  kind?: ShapeKind;
  width?: number;
  height?: number;
  radius?: number;
  rx?: number;
  ry?: number;
  points?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  strokeDashoffset?: number;
  cornerRadius?: number;
  starPoints?: number;
  innerRadius?: number;
  outerRadius?: number;
}

export class Shape extends SVGObject {
  public props: ShapeProps;
  private shapeElement: SVGElement | null = null;

  constructor(config: SVGObjectConfig & { props?: ShapeProps }) {
    super({ ...config, type: 'shape' });
    this.props = {
      kind: 'rect',
      width: 100,
      height: 100,
      radius: 50,
      rx: 50,
      ry: 50,
      fill: '#38bdf8',
      stroke: '#0284c7',
      strokeWidth: 2,
      cornerRadius: 0,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const kind = this.props.kind || 'rect';

    switch (kind) {
      case 'circle': {
        const c = Renderer.createElement('circle');
        Renderer.setAttributes(c, {
          cx: 0,
          cy: 0,
          r: this.props.radius || 50,
          fill: this.props.fill || '#38bdf8',
          stroke: this.props.stroke || 'none',
          'stroke-width': this.props.strokeWidth || 0,
          'stroke-dasharray': this.props.strokeDasharray || null,
          'stroke-dashoffset': this.props.strokeDashoffset ?? null
        });
        this.shapeElement = c;
        this.container.appendChild(c);
        break;
      }

      case 'ellipse': {
        const e = Renderer.createElement('ellipse');
        Renderer.setAttributes(e, {
          cx: 0,
          cy: 0,
          rx: this.props.rx || 60,
          ry: this.props.ry || 40,
          fill: this.props.fill || '#38bdf8',
          stroke: this.props.stroke || 'none',
          'stroke-width': this.props.strokeWidth || 0
        });
        this.shapeElement = e;
        this.container.appendChild(e);
        break;
      }

      case 'star': {
        const p = Renderer.createElement('polygon');
        const points = this.generateStarPoints(
          this.props.starPoints || 5,
          this.props.outerRadius || 50,
          this.props.innerRadius || 25
        );
        Renderer.setAttributes(p, {
          points,
          fill: this.props.fill || '#fbbf24',
          stroke: this.props.stroke || '#f59e0b',
          'stroke-width': this.props.strokeWidth || 2
        });
        this.shapeElement = p;
        this.container.appendChild(p);
        break;
      }

      case 'grid': {
        const g = Renderer.createElement('g');
        const w = this.props.width || 400;
        const h = this.props.height || 300;
        const step = 40;
        let d = '';
        for (let x = 0; x <= w; x += step) {
          d += `M ${x} 0 L ${x} ${h} `;
        }
        for (let y = 0; y <= h; y += step) {
          d += `M 0 ${y} L ${w} ${y} `;
        }
        const path = Renderer.createElement('path');
        Renderer.setAttributes(path, {
          d,
          fill: 'none',
          stroke: this.props.stroke || 'rgba(148, 163, 184, 0.2)',
          'stroke-width': 1
        });
        g.appendChild(path);
        this.shapeElement = g;
        this.container.appendChild(g);
        break;
      }

      case 'rect':
      default: {
        const r = Renderer.createElement('rect');
        Renderer.setAttributes(r, {
          x: 0,
          y: 0,
          width: this.props.width || 100,
          height: this.props.height || 100,
          rx: this.props.rx || this.props.cornerRadius || 0,
          ry: this.props.ry || this.props.cornerRadius || 0,
          fill: this.props.fill || '#38bdf8',
          stroke: this.props.stroke || 'none',
          'stroke-width': this.props.strokeWidth || 0,
          'stroke-dasharray': this.props.strokeDasharray || null,
          'stroke-dashoffset': this.props.strokeDashoffset ?? null
        });
        this.shapeElement = r;
        this.container.appendChild(r);
        break;
      }
    }
  }

  private generateStarPoints(numPoints: number, outerR: number, innerR: number): string {
    let pts = '';
    const angleStep = Math.PI / numPoints;
    for (let i = 0; i < 2 * numPoints; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const currAngle = i * angleStep - Math.PI / 2;
      const px = Math.cos(currAngle) * r;
      const py = Math.sin(currAngle) * r;
      pts += `${px.toFixed(2)},${py.toFixed(2)} `;
    }
    return pts.trim();
  }

  public setFill(fill: string): void {
    this.props.fill = fill;
    if (this.shapeElement) {
      this.shapeElement.setAttribute('fill', fill);
    }
  }

  public setStroke(stroke: string, strokeWidth?: number): void {
    this.props.stroke = stroke;
    if (strokeWidth !== undefined) this.props.strokeWidth = strokeWidth;
    if (this.shapeElement) {
      this.shapeElement.setAttribute('stroke', stroke);
      if (strokeWidth !== undefined) this.shapeElement.setAttribute('stroke-width', String(strokeWidth));
    }
  }
}
